// ============================================================
// GridV2 — カレンダーグリッドのメインコンテナ
// ============================================================

import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import { Vehicle, Reservation, VehicleType, StatusItem } from './types';
import GridCanvas from './GridCanvas';
import { useCalendarInteraction, absoluteYToDate } from './useCalendarInteraction';
import { useFinder } from './useFinder';
import { useVehicleFilter } from './useVehicleFilter';
import { useMoveSwap, appendMemoLog } from './useMoveSwap';
import { diffInDays, DAY_HEIGHT_PX, dateToAbsoluteY } from './useGridGeometry';
import { GRID_CONFIG } from './types';
import ReservationEditorPanel from '../editor2';
// @ts-ignore
import VehicleDetailPanel from '../VehicleDetailPanel';
// @ts-ignore
import { saveDispatchReservation } from '../../api/dispatchService';
// @ts-ignore
import { updateVehicle as updateVehicleApi } from '../../api/vehicleService';
// @ts-ignore
import { reservationSyncService } from '../../services/reservationSyncService';

const INITIAL_PAST_DAYS = 14;
const INITIAL_FUTURE_DAYS = 365;
const PAST_LOAD_CHUNK = 60; // 過去スクロール時に追加する日数

function getInitialRangeStart(): Date {
  const d = new Date();
  d.setDate(d.getDate() - INITIAL_PAST_DAYS);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getInitialRangeEnd(): Date {
  const d = new Date();
  d.setDate(d.getDate() + INITIAL_FUTURE_DAYS);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** 読み込み済み期間 */
interface LoadedRange {
  start: Date;
  end: Date;
}

interface Props {
  vehicles: Vehicle[];
  reservations: Reservation[];
  vehicleTypes: VehicleType[];
  vehicleStatusList: StatusItem[];
  reservationStatusList: StatusItem[];
  locationStatusList?: StatusItem[];
  user?: any;
}

type EditingReservation = Reservation & { __isNew?: boolean };

export default function GridV2({
  vehicles,
  reservations,
  vehicleTypes,
  vehicleStatusList,
  reservationStatusList,
  locationStatusList = [],
  user,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const todayScrollRef = useRef<(() => void) | null>(null);
  const ghostPositionRef = useRef<any>(null);
  const navigate = useNavigate();

  // 動的レンジ管理
  const [rangeStart, setRangeStart] = useState(getInitialRangeStart);
  const [rangeEnd, setRangeEnd] = useState(getInitialRangeEnd);
  const totalDays = useMemo(() => diffInDays(rangeStart, rangeEnd) + 1, [rangeStart, rangeEnd]);
  const [loadedRanges, setLoadedRanges] = useState<LoadedRange[]>(() => [
    { start: getInitialRangeStart(), end: getInitialRangeEnd() }
  ]);
  const isLoadingRangeRef = useRef(false);
  const initialScrollDoneRef = useRef(false);

  // 編集パネル state
  const [editing, setEditing] = useState<EditingReservation | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // パネル切り替え時のアニメーションはReservationEditor内部で制御
  // 親は setEditing(newData) するだけでOK

  // 編集中かどうかを追跡（開いた時のスナップショットと比較）
  const editingOriginalRef = useRef<string | null>(null);
  const editingIdRef = useRef<string | null>(null);
  useEffect(() => {
    const currentId = editing ? String(editing.id ?? editing.page_id ?? '') : null;
    if (editing && currentId !== editingIdRef.current) {
      // 新しい予約を開いた → スナップショット保存
      editingOriginalRef.current = JSON.stringify(editing);
      editingIdRef.current = currentId;
    } else if (!editing) {
      editingOriginalRef.current = null;
      editingIdRef.current = null;
    }
  }, [editing]);
  const isEditingDirty = useCallback(() => {
    if (!editing || !editingOriginalRef.current) return false;
    return JSON.stringify(editing) !== editingOriginalRef.current;
  }, [editing]);

  // 車両パネル state
  const [vehiclePanel, setVehiclePanel] = useState<Vehicle | null>(null);
  const [vehiclePanelDirty, setVehiclePanelDirty] = useState(false);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);

  // 車両選択モード state
  const [vehicleSelectMode, setVehicleSelectMode] = useState(false);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<Set<string>>(new Set());
  const [batchLabel, setBatchLabel] = useState('');
  const [batchMark, setBatchMark] = useState('');
  const [batchBgColor, setBatchBgColor] = useState('');
  const [batchTextColor, setBatchTextColor] = useState('');
  const [isBatchSaving, setIsBatchSaving] = useState(false);
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);
  const [batchApplyFlags, setBatchApplyFlags] = useState({ label: true, mark: true, bg_color: true, text_color: true });

  // hover
  const [hoveredReservationId, setHoveredReservationId] = useState<string | null>(null);
  const [hoveredHeaderIndex, setHoveredHeaderIndex] = useState<number | null>(null);

  // ヘッダー DnD（車両並べ替え）
  const [headerDragIndex, setHeaderDragIndex] = useState<number | null>(null);
  const [headerDropTarget, setHeaderDropTarget] = useState<number | null>(null);

  // ズーム（Cookie 保存）
  const ZOOM_COOKIE = 'calv2_zoom';
  const ZOOM_MIN = 0.4;
  const ZOOM_MAX = 1.5;
  const ZOOM_STEP = 0.1;
  const [zoom, setZoom] = useState(() => {
    try {
      const raw = document.cookie.split('; ').find(c => c.startsWith(`${ZOOM_COOKIE}=`));
      if (raw) {
        const v = parseFloat(raw.split('=')[1]);
        if (v >= ZOOM_MIN && v <= ZOOM_MAX) return v;
      }
    } catch {}
    return 1;
  });
  useEffect(() => {
    document.cookie = `${ZOOM_COOKIE}=${zoom}; path=/; max-age=${60 * 60 * 24 * 365}`;
  }, [zoom]);

  /** ビューポート中央を基準にズーム変更 */
  const handleZoom = useCallback((newZoom: number) => {
    const el = containerRef.current;
    if (!el) { setZoom(newZoom); return; }
    const oldZoom = zoom;
    // ビューポート中央の論理座標を算出
    const centerX = (el.scrollLeft + el.clientWidth / 2) / oldZoom;
    const centerY = (el.scrollTop + el.clientHeight / 2) / oldZoom;
    setZoom(newZoom);
    // 新しいズームで同じ論理座標がビューポート中央に来るようスクロール
    requestAnimationFrame(() => {
      el.scrollLeft = centerX * newZoom - el.clientWidth / 2;
      el.scrollTop = centerY * newZoom - el.clientHeight / 2;
    });
  }, [zoom]);

  // トースト通知
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error'; onUndo?: () => void } | null>(null);
  const showToast = useCallback((message: string, severity: 'success' | 'error' = 'success', onUndo?: () => void) => {
    setToast({ message, severity, onUndo });
  }, []);

  // 車両ソート: 車種優先度(降順) → 車両優先度(降順) → 車両コード(昇順)
  const sortedVehicles = useMemo(() => {
    const typePriorityMap = new Map<string, number>();
    for (const vt of vehicleTypes) {
      typePriorityMap.set(String(vt.id), vt.priority ?? 0);
    }
    return [...vehicles].sort((a, b) => {
      const aPri = typePriorityMap.get(String(a.car_type_id)) ?? 0;
      const bPri = typePriorityMap.get(String(b.car_type_id)) ?? 0;
      if (aPri !== bPri) return bPri - aPri;
      const aResPri = a.priority ?? 0;
      const bResPri = b.priority ?? 0;
      if (aResPri !== bResPri) return bResPri - aResPri;
      return String(a.vehicle_code || '').localeCompare(String(b.vehicle_code || ''));
    });
  }, [vehicles, vehicleTypes]);

  // 車両フィルタ
  const {
    filteredVehicles,
    filterOpen,
    setFilterOpen,
    activeTypeIds,
    activeStatusIds,
    showOutOfUse,
    toggleType,
    toggleStatus,
    toggleOutOfUse,
    selectAll,
    clearAll,
  } = useVehicleFilter({ vehicles: sortedVehicles, vehicleTypes, vehicleStatusList });

  // ファインダー（検索）
  const {
    query,
    setQuery,
    results: finderResults,
    currentIndex: finderIndex,
    currentReservation: finderReservation,
    currentVehicle: finderVehicle,
    next: finderNext,
    prev: finderPrev,
    clear: finderClear,
  } = useFinder({
    reservations: useMemo(() => {
      const DISPLAY_STATUSES = new Set(['booked', 'done', 'in_use', 'mail_ready', 'received', 'pending', 'offer', 'request']);
      const vehicleIds = new Set(filteredVehicles.map(v => String(v.id)));
      return reservations.filter(r => vehicleIds.has(String(r.vehicle_id)) && DISPLAY_STATUSES.has(r.status));
    }, [reservations, filteredVehicles]),
    vehicles: filteredVehicles,
  });

  const highlightedReservationId = finderReservation?.id ?? null;
  const highlightedVehicleIdx = finderVehicle?.index ?? null;

  // 移動・入替
  const {
    mode: interactionMode,
    selectedReservation: moveSwapSelected,
    enterMove,
    enterSwap,
    enterVehicleSwap,
    startMove,
    startSwap,
    executeMove,
    executeSwap,
    cancel: cancelMoveSwap,
    undoAction,
    dismissUndo,
    vehicleSwapSource,
    vehicleSwapInfo,
    selectVehicleSwapSource,
    selectVehicleSwapTarget,
    executeVehicleSwap,
    cancelVehicleSwap,
  } = useMoveSwap({
    reservations,
    vehicles: filteredVehicles,
    onComplete: () => {},
    onToast: showToast,
  });

  // Escape キー: パネル / 移動入替モード を閉じる
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (vehicleSelectMode) {
        setVehicleSelectMode(false);
        setSelectedVehicleIds(new Set());
        return;
      }
      if (editing) { setEditing(null); return; }
      if (interactionMode !== 'none') { cancelMoveSwap(); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [vehicleSelectMode, editing, interactionMode, cancelMoveSwap]);

  // 検索結果へスクロール
  const scrollToHighlighted = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const z = zoom || 1;
    const { leftGutterWidth: gw, headerHeight: hh, columnWidth: cw } = GRID_CONFIG;

    // スクロール座標の計算:
    // Canvas draw() は bodyScrollX = max(0, scrollX/z - gw), bodyScrollY = max(0, scrollY/z - hh)
    // 列 vi をガター右端に表示: scrollX = (gw + vi * cw) * z
    // absY をヘッダー直下に表示: scrollY = (hh + absY) * z

    // 車両マッチ → 横スクロールで車両列を表示（縦は維持）
    if (finderVehicle) {
      const vi = finderVehicle.index;
      const targetX = (gw + vi * cw) * z;
      el.scrollTo({ left: targetX, top: el.scrollTop, behavior: 'smooth' });
      return;
    }

    const r = finderReservation;
    if (!r) return;
    const startDate = new Date(r.start_date);
    if (isNaN(startDate.getTime())) { return; }
    const dayIdx = diffInDays(rangeStart, startDate);

    if (dayIdx < 0) { return; }

    // 縦: 予約がビューポート上 1/3 に来るようスクロール
    const absY = dayIdx * DAY_HEIGHT_PX;
    const targetY = (hh + absY) * z - el.clientHeight / 3;

    // 横: 予約の車両列がビューポートに見えるようスクロール
    const vid = String(r.vehicle_id);
    const vi = filteredVehicles.findIndex(v => String(v.id) === vid);
    let targetX = el.scrollLeft;
    if (vi >= 0) {
      const colScrollLeft = (gw + vi * cw) * z;         // 列がガター右端に来る scrollX
      const colScrollRight = (gw + (vi + 1) * cw) * z;  // 列の右端
      const viewLeft = el.scrollLeft + gw * z;
      const viewRight = el.scrollLeft + el.clientWidth;
      // 列がビューポート外なら移動
      if (colScrollLeft < viewLeft || colScrollRight > viewRight) {
        targetX = colScrollLeft;
      }
    }

    el.scrollTo({ top: Math.max(0, targetY), left: Math.max(0, targetX), behavior: 'smooth' });
  }, [finderReservation, finderVehicle, rangeStart, zoom, filteredVehicles]);

  // 検索結果が変わったら自動スクロール
  const prevFinderKeyRef = useRef('');
  useEffect(() => {
    const key = finderReservation?.id ?? (finderVehicle ? `v${finderVehicle.index}` : '');
    if (key && key !== prevFinderKeyRef.current) {
      prevFinderKeyRef.current = key;
      scrollToHighlighted();
    }
  }, [finderReservation, finderVehicle, scrollToHighlighted]);

  const handleFinderNext = useCallback(() => { finderNext(); }, [finderNext]);
  const handleFinderPrev = useCallback(() => { finderPrev(); }, [finderPrev]);

  // vehicleMap（Object形式）— ReservationEditorPanel が要求する形式
  const vehicleMap = useMemo(() => {
    const map: Record<string, Vehicle> = {};
    for (const v of vehicles) map[String(v.id)] = v;
    return map;
  }, [vehicles]);

  // 予約クリック: 通常クリック → 移動モード（水平移動）ソース選択
  // 移動モード中でソース選択済み → 移動先実行
  // 入替モード中 → 入替フロー
  const handleReservationClick = useCallback((r: Reservation) => {
    // 新規予約ゴーストのクリックは無視
    if (String(r.id).startsWith('tmp_')) return;
    const isMove = interactionMode === 'move' || interactionMode === 'move-free';
    if (isMove) {
      if (!moveSwapSelected) {
        startMove(r);
      } else {
        // 自由移動: ゴースト位置の日時を使用、水平移動: ターゲット予約の車両
        const gp = ghostPositionRef.current;
        if (interactionMode === 'move-free' && gp) {
          const targetDate = absoluteYToDate(rangeStart, gp.absoluteY);
          const targetVid = String(filteredVehicles[gp.vehicleIndex]?.id || r.vehicle_id);
          executeMove(targetVid, targetDate);
        } else {
          executeMove(r.vehicle_id, new Date(r.start_date));
        }
      }
      return;
    }
    if (interactionMode === 'swap') {
      if (!moveSwapSelected) {
        startSwap(r);
      } else {
        executeSwap(r);
      }
      return;
    }
    // 通常クリック → 移動モードでソース選択
    startMove(r);
  }, [interactionMode, moveSwapSelected, startMove, startSwap, executeMove, executeSwap, filteredVehicles, rangeStart]);

  // 空セルクリック: 移動モード(ソース選択済み)なら移動実行、通常は新規予約
  const handleEmptyCellClick = useCallback((vehicleId: string, date: Date) => {
    // 新規予約編集中は空セルクリックで新たな予約を作成しない
    if (editing && editing.__isNew) return;
    // 入替モードで空セルクリック → 対象なしでキャンセル
    if (interactionMode === 'swap') {
      showToast('入替対象が見つかりません', 'error');
      cancelMoveSwap();
      return;
    }
    if ((interactionMode === 'move' || interactionMode === 'move-free') && moveSwapSelected) {
      if (interactionMode === 'move-free') {
        const gp = ghostPositionRef.current;
        if (gp) {
          const targetDate = absoluteYToDate(rangeStart, gp.absoluteY);
          const targetVid = String(filteredVehicles[gp.vehicleIndex]?.id || vehicleId);
          executeMove(targetVid, targetDate);
          return;
        }
      }
      executeMove(vehicleId, date);
      return;
    }
    // 既存予約の編集中 → 編集済みなら破棄確認、未編集なら閉じ→開き
    if (editing && !editing.__isNew) {
      if (isEditingDirty()) {
        if (!window.confirm('編集中の内容が破棄されます。新規予約を作成しますか？')) return;
      }
    }
    const vehicle = filteredVehicles.find(v => String(v.id) === vehicleId);
    const endDate = new Date(date.getTime() + 30 * 60000);
    const newReservation = {
      id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      vehicle_id: vehicleId,
      start_date: date.toISOString(),
      end_date: endDate.toISOString(),
      customer_name: '',
      status: 'booked',
      bg_color: null,
      text_color: null,
      car_type_id: vehicle?.car_type_id || '',
      __isNew: true,
    } as EditingReservation;
    setEditing(newReservation);
  }, [interactionMode, moveSwapSelected, executeMove, editing, filteredVehicles, isEditingDirty]);

  // リサイズ（overlay 上下端ドラッグ）
  const handleReservationResize = useCallback(async (r: Reservation, edge: 'top' | 'bottom', newDate: Date) => {
    // 新規予約ゴーストのリサイズ → editing のみ更新、API 呼び出しなし
    // ※ editing クロージャは古い可能性があるため、ID パターンで判定
    if (String(r.id).startsWith('tmp_')) {
      if (edge === 'top') {
        setEditing(prev => prev ? { ...prev, start_date: newDate.toISOString() } : null);
      } else {
        setEditing(prev => prev ? { ...prev, end_date: newDate.toISOString() } : null);
      }
      return;
    }
    const original = { ...r };
    const oldDate = edge === 'top' ? r.start_date : r.end_date;
    let updated = { ...r };
    if (edge === 'top') {
      updated.start_date = newDate.toISOString();
    } else {
      updated.end_date = newDate.toISOString();
    }

    // メモ追記
    const label = edge === 'top' ? '貸出日時変更' : '返却日時変更';
    const oldShort = new Date(oldDate).toLocaleString('ja', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const newShort = newDate.toLocaleString('ja', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    updated = appendMemoLog(updated, label, `${oldShort} → ${newShort}`);

    // 楽観的更新
    const patch = (data: Reservation[]) => {
      for (const d of data) {
        reservationSyncService.cachedReservations.set(String(d.id), d);
      }
      const all = reservationSyncService.getReservations();
      reservationSyncService.listeners.forEach((cb: Function) => cb([], all));
    };

    patch([updated]);

    // Undo 付きトースト
    const undoFn = async () => {
      patch([original]);
      try {
        await saveDispatchReservation(original);
        // Pusher 即時更新のため forceSync 不要
        showToast('元に戻しました');
      } catch {
        showToast('元に戻す操作に失敗しました', 'error');
      }
    };

    try {
      await saveDispatchReservation(updated);
      // Pusher 即時更新のため forceSync 不要
      showToast(
        edge === 'top' ? '貸出日時を変更しました' : '返却日時を変更しました',
        'success',
        undoFn,
      );
    } catch (e) {
      patch([original]);
      showToast('更新に失敗しました', 'error');
    }
  }, [showToast]);

  // ドラッグで新規予約
  const handleDragCreate = useCallback((vehicleId: string, startDate: Date, endDate: Date) => {
    // 新規予約編集中はドラッグ作成しない
    if (editing && editing.__isNew) return;
    // 既存予約の編集中 → 編集済みなら破棄確認、未編集なら閉じ→開き
    if (editing && !editing.__isNew) {
      if (isEditingDirty()) {
        if (!window.confirm('編集中の内容が破棄されます。新規予約を作成しますか？')) return;
      }
    }
    const bookedStatus = reservationStatusList.find(s => s.code === 'booked');
    const vehicle = filteredVehicles.find(v => String(v.id) === vehicleId);
    const newReservation = {
      id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      vehicle_id: vehicleId,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      customer_name: '',
      status: bookedStatus ? String(bookedStatus.id) : 'booked',
      bg_color: null,
      text_color: null,
      car_type_id: vehicle?.car_type_id || '',
      __isNew: true,
    } as EditingReservation;
    setEditing(newReservation);
  }, [reservationStatusList, editing, filteredVehicles, isEditingDirty]);

  // 保存
  const handleSave = useCallback(async (opts: { keepOpen?: boolean } = {}) => {
    if (!editing || isSaving) return;
    const isNew = !!(editing as EditingReservation).__isNew;
    setIsSaving(true);
    if (isNew) showToast('予約を作成中...');
    try {
      const saved = await saveDispatchReservation(editing);
      // 楽観的更新: APIレスポンス(saved)をキャッシュに反映（updated_at が最新のため Pusher 競合を防止）
      if (isNew && saved?.id) {
        // 新規: tmp_ IDを削除し、実IDでキャッシュに追加
        reservationSyncService.cachedReservations.delete(String(editing.id));
        reservationSyncService.cachedReservations.set(String(saved.id), saved);
      } else if (!isNew && saved) {
        reservationSyncService.cachedReservations.set(String(editing.id), saved);
      } else if (!isNew) {
        reservationSyncService.cachedReservations.set(String(editing.id), editing);
      }
      const all = reservationSyncService.getReservations();
      reservationSyncService.listeners.forEach((cb: Function) => cb([], all));
      // Pusher 即時更新のため forceSync 不要
      showToast(isNew ? '予約を作成しました' : '予約を保存しました');
      if (!opts.keepOpen) {
        if (isNew) {
          setTimeout(() => setEditing(null), 1500);
        } else {
          setEditing(null);
        }
      }
    } catch (e) {
      console.error('[GridV2] save failed:', e);
      showToast('保存に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [editing, isSaving, showToast]);

  // --- 車両選択モード ---
  const toggleVehicleSelect = useCallback((vehicleId: string) => {
    setSelectedVehicleIds(prev => {
      const next = new Set(prev);
      if (next.has(vehicleId)) {
        next.delete(vehicleId);
      } else {
        // 最初の選択時 → その車両のjson_data値を入力欄の初期値に
        if (prev.size === 0) {
          const v = filteredVehicles.find((vv: Vehicle) => String(vv.id) === vehicleId);
          const jd = (v as any)?.json_data?.data;
          if (jd) {
            setBatchLabel(jd.label || '');
            setBatchMark(jd.mark || '');
            setBatchBgColor(jd.bg_color || '');
            setBatchTextColor(jd.text_color || '');
          }
        }
        next.add(vehicleId);
      }
      return next;
    });
  }, [filteredVehicles]);

  const enterVehicleSelectMode = useCallback(() => {
    if (interactionMode !== 'none') cancelMoveSwap();
    setVehicleSelectMode(true);
    setSelectedVehicleIds(new Set());
    setBatchLabel('');
    setBatchMark('');
    setBatchBgColor('');
    setBatchTextColor('');
  }, [interactionMode, cancelMoveSwap]);

  const exitVehicleSelectMode = useCallback(() => {
    setVehicleSelectMode(false);
    setSelectedVehicleIds(new Set());
  }, []);

  // 保存ボタン → 確認ダイアログを開く
  const handleBatchSaveClick = useCallback(() => {
    if (selectedVehicleIds.size === 0) return;
    setBatchConfirmOpen(true);
  }, [selectedVehicleIds]);

  // 確認ダイアログで「適用」を押した時の実行
  const executeBatchSave = useCallback(async () => {
    if (selectedVehicleIds.size === 0 || isBatchSaving) return;
    setBatchConfirmOpen(false);
    setIsBatchSaving(true);
    const flags = batchApplyFlags;
    try {
      for (const vehicleId of selectedVehicleIds) {
        const vehicle = filteredVehicles.find((v: Vehicle) => String(v.id) === vehicleId);
        const existing = (vehicle as any)?.json_data || { data: {}, types: {} };
        const merged = {
          data: {
            ...existing.data,
            ...(flags.label ? { label: batchLabel } : {}),
            ...(flags.mark ? { mark: batchMark } : {}),
            ...(flags.bg_color ? { bg_color: batchBgColor } : {}),
            ...(flags.text_color ? { text_color: batchTextColor } : {}),
          },
          types: { ...existing.types, label: 'text', mark: 'color', bg_color: 'color', text_color: 'color' },
        };
        await updateVehicleApi({ id: vehicleId, json_data: merged }, user?.id);
      }
      showToast(`${selectedVehicleIds.size}台の車両を更新しました`);
      exitVehicleSelectMode();
    } catch (e) {
      console.error('[GridV2] batch save failed:', e);
      showToast('一括保存に失敗しました', 'error');
    } finally {
      setIsBatchSaving(false);
    }
  }, [selectedVehicleIds, isBatchSaving, filteredVehicles, batchLabel, batchMark, batchBgColor, batchTextColor, batchApplyFlags, user, showToast, exitVehicleSelectMode]);

  // 車両ヘッダークリック
  const handleHeaderClick = useCallback((v: Vehicle, shiftKey?: boolean, metaKey?: boolean) => {
    // 車両選択モード中 → トグル選択
    if (vehicleSelectMode) {
      toggleVehicleSelect(String(v.id));
      return;
    }
    // vehicle-swap モード中 → ソース/ターゲット選択
    if (interactionMode === 'vehicle-swap') {
      if (!vehicleSwapSource) {
        selectVehicleSwapSource(v);
      } else {
        selectVehicleSwapTarget(v);
      }
      return;
    }
    // Shift+Cmd+Click → vehicle-swap モードに入り、即ソース選択
    if (shiftKey && metaKey) {
      enterVehicleSwap();
      selectVehicleSwapSource(v);
      return;
    }
    setVehiclePanel(v);
  }, [vehicleSelectMode, toggleVehicleSelect, interactionMode, vehicleSwapSource, selectVehicleSwapSource, selectVehicleSwapTarget, enterVehicleSwap]);

  // 車両パネル保存
  const handleVehicleSave = useCallback(async (vehicleData: any) => {
    try {
      await updateVehicleApi(vehicleData, user?.id);
      showToast('車両情報を保存しました');
      // Context の車両データを更新するため forceSync
      // Pusher 即時更新のため forceSync 不要
      setVehiclePanel(null);
    } catch (e) {
      console.error('[GridV2] vehicle save failed:', e);
      showToast('車両の保存に失敗しました', 'error');
    }
  }, [user, showToast]);

  // ヘッダー DnD ドロップ → 優先度再計算・保存
  const handleHeaderDrop = useCallback(async (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const reordered = [...filteredVehicles];
    const [moved] = reordered.splice(fromIndex, 1);
    // toIndex は「この位置の前に挿入」
    const insertAt = toIndex > fromIndex ? toIndex - 1 : toIndex;
    reordered.splice(insertAt, 0, moved);

    // 変更された車両の優先度を再計算（降順: 一番左が最大）
    const total = reordered.length;
    const updates: { id: string; priority: number }[] = [];
    for (let i = 0; i < total; i++) {
      const newPriority = total - i;
      if ((reordered[i].priority ?? 0) !== newPriority) {
        updates.push({ id: String(reordered[i].id), priority: newPriority });
      }
    }
    if (updates.length === 0) return;

    showToast(`車両順番を更新中...（${updates.length}件）`);
    try {
      // 5件ずつバッチ保存
      for (let i = 0; i < updates.length; i += 5) {
        const batch = updates.slice(i, i + 5);
        await Promise.all(batch.map(u => updateVehicleApi({ id: u.id, priority: u.priority }, user?.id)));
      }
      // Pusher 即時更新のため forceSync 不要
      showToast('車両順番を更新しました');
    } catch (e) {
      console.error('[GridV2] header reorder failed:', e);
      showToast('車両順番の更新に失敗しました', 'error');
    }
  }, [filteredVehicles, user, showToast]);

  // Canvas クリック処理
  // alt+Click → 自由移動モード
  const handleCmdReservationClick = useCallback((r: Reservation, shiftKey?: boolean, freeMove?: boolean) => {
    if (freeMove) {
      startMove(r, true); // freeMove flag
    } else if (shiftKey) {
      startSwap(r); // Shift+Click → 入替モード
    } else {
      startMove(r);
    }
  }, [startMove]);

  // ダブルクリック → 編集パネル
  // アニメーションはReservationEditor内部で制御（ID変化を検知して閉じ→開き）
  const handleReservationDblClick = useCallback((r: Reservation) => {
    cancelMoveSwap();
    setEditing({ ...r });
  }, [cancelMoveSwap]);

  // ソース予約のブロック高さ＋Y座標（ゴースト表示用）
  const { selectedBlockHeight, selectedAbsoluteY } = useMemo(() => {
    if (!moveSwapSelected) return { selectedBlockHeight: 0, selectedAbsoluteY: 0 };
    const s = new Date(moveSwapSelected.start_date);
    const e = new Date(moveSwapSelected.end_date);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return { selectedBlockHeight: 0, selectedAbsoluteY: 0 };
    const top = dateToAbsoluteY(rangeStart, s);
    const bottom = dateToAbsoluteY(rangeStart, e);
    return { selectedBlockHeight: Math.max(8, bottom - top), selectedAbsoluteY: top };
  }, [moveSwapSelected, rangeStart]);

  // 新規予約編集中のゴースト（白い半透明オーバーレイ）
  const editingGhost = useMemo(() => {
    if (!editing || !editing.__isNew) return null;
    const vid = String(editing.vehicle_id);
    const vi = filteredVehicles.findIndex(v => String(v.id) === vid);
    if (vi < 0) return null;
    const s = new Date(editing.start_date);
    const e = new Date(editing.end_date);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
    const top = dateToAbsoluteY(rangeStart, s);
    const bottom = dateToAbsoluteY(rangeStart, e);
    return { vehicleIndex: vi, absoluteY: top, blockHeight: Math.max(8, bottom - top) };
  }, [editing, filteredVehicles, rangeStart]);

  // 新規予約ゴーストのリサイズ → editing の日時を更新
  const handleEditingGhostResize = useCallback((r: Reservation, edge: 'top' | 'bottom', newDate: Date) => {
    if (!editing || !editing.__isNew) return;
    // 編集中の予約IDと一致する場合のみ
    if (String(r.id) !== String(editing.id)) return;
    if (edge === 'top') {
      setEditing(prev => prev ? { ...prev, start_date: newDate.toISOString() } : null);
    } else {
      setEditing(prev => prev ? { ...prev, end_date: newDate.toISOString() } : null);
    }
  }, [editing]);

  // 新規予約編集中は reservations に一時的に追加（リサイズ判定用）
  const interactionReservations = useMemo(() => {
    if (editing && editing.__isNew) return [...reservations, editing as Reservation];
    return reservations;
  }, [reservations, editing]);

  const { ghostPosition, dragGhost, resizeGhost } = useCalendarInteraction({
    containerRef,
    vehicles: filteredVehicles,
    reservations: interactionReservations,
    rangeStart,
    dayCount: totalDays,
    onReservationClick: handleReservationClick,
    onEmptyCellClick: handleEmptyCellClick,
    onCmdReservationClick: handleCmdReservationClick,
    onReservationDblClick: handleReservationDblClick,
    onHeaderClick: handleHeaderClick,
    onDragCreate: handleDragCreate,
    onReservationResize: handleReservationResize,
    interactionMode,
    selectedBlockHeight,
    selectedAbsoluteY,
    hoveredReservationId,
    zoom,
    onHeaderDragStart: setHeaderDragIndex,
    onHeaderDragOver: setHeaderDropTarget,
    onHeaderDrop: handleHeaderDrop,
    onHeaderDragEnd: () => { setHeaderDragIndex(null); setHeaderDropTarget(null); },
  });
  ghostPositionRef.current = ghostPosition;

  const handleTodayClick = () => todayScrollRef.current?.();

  // ---------- 動的レンジ: 過去スクロール & 日付ジャンプ ----------

  /** 指定期間のデータを読み込んでレンジを拡張 */
  const loadRange = useCallback(async (loadStart: Date, loadEnd: Date) => {
    if (isLoadingRangeRef.current) return;
    isLoadingRangeRef.current = true;
    try {
      showToast('データを読み込み中...', 'success');
      await reservationSyncService.syncRange(loadStart, loadEnd);
      setLoadedRanges(prev => [...prev, { start: loadStart, end: loadEnd }]);
      showToast('データを読み込みました', 'success');
    } catch {
      showToast('データの読み込みに失敗しました', 'error');
    } finally {
      isLoadingRangeRef.current = false;
    }
  }, [showToast]);

  /** 過去スクロール検知 — 上端到達時に2ヶ月分追加 */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = () => {
      // 初回スクロール完了前は過去読み込みを抑制
      if (!(el as any).__initialScrollDone) return;
      const scrollTop = el.scrollTop;
      // ヘッダー高さ + 3日分以内にスクロールしたら追加読み込み
      if (scrollTop < GRID_CONFIG.headerHeight + DAY_HEIGHT_PX * 3 && !isLoadingRangeRef.current) {
        const newStart = new Date(rangeStart);
        newStart.setDate(newStart.getDate() - PAST_LOAD_CHUNK);
        const oldStart = new Date(rangeStart);
        // レンジを拡張
        setRangeStart(new Date(newStart));
        // スクロール位置を維持（追加された日数分だけ下にオフセット）
        requestAnimationFrame(() => {
          el.scrollTop = scrollTop + PAST_LOAD_CHUNK * DAY_HEIGHT_PX;
        });
        // データを読み込み
        loadRange(newStart, oldStart);
      }
    };
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [rangeStart, loadRange]);

  // 日付ジャンプ
  const handleDateJump = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val || !containerRef.current) return;
    const date = new Date(val);
    if (isNaN(date.getTime())) return;

    // 日付がレンジ外ならレンジを拡張してデータを読み込む
    const jumpStart = new Date(date);
    jumpStart.setDate(jumpStart.getDate() - 14);
    const jumpEnd = new Date(date);
    jumpEnd.setDate(jumpEnd.getDate() + 30);

    let newRangeStart = rangeStart;
    let newRangeEnd = rangeEnd;
    let needsLoad = false;

    if (jumpStart < rangeStart) {
      newRangeStart = new Date(jumpStart);
      needsLoad = true;
    }
    if (jumpEnd > rangeEnd) {
      newRangeEnd = new Date(jumpEnd);
      needsLoad = true;
    }

    if (needsLoad) {
      setRangeStart(newRangeStart);
      setRangeEnd(newRangeEnd);
      loadRange(jumpStart, jumpEnd);
    }

    // スクロール（レンジ更新後に実行）
    setTimeout(() => {
      if (!containerRef.current) return;
      const dayIdx = diffInDays(newRangeStart, date);
      if (dayIdx < 0) return;
      const targetY = GRID_CONFIG.headerHeight + Math.max(0, dayIdx * DAY_HEIGHT_PX - containerRef.current.clientHeight / 3);
      containerRef.current.scrollTo({ top: targetY, behavior: 'smooth' });
    }, needsLoad ? 300 : 0);
  }, [rangeStart, rangeEnd, loadRange]);

  const isFilterActive = activeTypeIds.size < vehicleTypes.length ||
    activeStatusIds.size < vehicleStatusList.length ||
    showOutOfUse;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#0f172a',
        color: '#e2e8f0',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* ツールバー */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          background: '#0f172a',
          borderBottom: '1px solid #1e293b',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', whiteSpace: 'nowrap' }}>
          予約カレンダー v2
        </span>
        <span style={{ fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
          {filteredVehicles.length} 台 / {reservations.length} 件
        </span>

        {/* ファインダー */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
          <input
            type="text"
            placeholder="顧客名・予約コード検索..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { if (e.shiftKey) handleFinderPrev(); else handleFinderNext(); }
              if (e.key === 'Escape') finderClear();
            }}
            style={{
              padding: '3px 8px',
              background: '#1e293b',
              border: query ? '1px solid #3b82f6' : '1px solid #334155',
              borderRadius: 6,
              color: '#e2e8f0',
              fontSize: 12,
              width: 180,
              outline: 'none',
            }}
          />
          {finderResults.length > 0 && (
            <>
              <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                {finderIndex + 1}/{finderResults.length}
              </span>
              <button onClick={handleFinderPrev} style={toolbarBtnStyle}>‹</button>
              <button onClick={handleFinderNext} style={toolbarBtnStyle}>›</button>
            </>
          )}
          {query && (
            <button onClick={finderClear} style={{ ...toolbarBtnStyle, color: '#94a3b8' }}>✕</button>
          )}
        </div>

        {/* 日付ジャンプ */}
        <input
          type="date"
          onChange={handleDateJump}
          style={{
            padding: '3px 6px',
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: 6,
            color: '#e2e8f0',
            fontSize: 12,
            outline: 'none',
          }}
        />

        {/* フィルタボタン */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            style={{
              padding: '4px 10px',
              background: isFilterActive ? '#1d4ed8' : '#1e293b',
              color: isFilterActive ? '#fff' : '#94a3b8',
              border: '1px solid #334155',
              borderRadius: 6,
              fontSize: 12,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            フィルタ{isFilterActive ? ' ●' : ''}
          </button>

          {filterOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 100,
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: 8,
                padding: '12px',
                minWidth: 220,
                marginTop: 4,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>車種</div>
                {vehicleTypes.map(vt => (
                  <label key={vt.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 3, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={activeTypeIds.has(String(vt.id))}
                      onChange={() => toggleType(String(vt.id))}
                    />
                    {vt.name}
                  </label>
                ))}
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>車両ステータス</div>
                {vehicleStatusList.map(s => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 3, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={activeStatusIds.has(s.id)}
                      onChange={() => toggleStatus(s.id)}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 8, cursor: 'pointer' }}>
                <span style={{ position: 'relative', display: 'inline-block', width: 34, height: 18, flexShrink: 0 }}>
                  <input type="checkbox" checked={showOutOfUse} onChange={toggleOutOfUse} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                  <span style={{
                    position: 'absolute', inset: 0, borderRadius: 9, background: showOutOfUse ? '#3b82f6' : '#475569', transition: 'background 0.2s',
                  }} />
                  <span style={{
                    position: 'absolute', top: 2, left: showOutOfUse ? 18 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                  }} />
                </span>
                アーカイブを表示
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={selectAll} style={{ ...toolbarBtnStyle, flex: 1, padding: '4px 0' }}>全選択</button>
                <button onClick={clearAll} style={{ ...toolbarBtnStyle, flex: 1, padding: '4px 0' }}>クリア</button>
              </div>
              <button
                onClick={() => setFilterOpen(false)}
                style={{ ...toolbarBtnStyle, width: '100%', marginTop: 6, padding: '5px 0', background: '#3b82f6', color: '#fff', fontWeight: 600 }}
              >保存</button>
            </div>
          )}
        </div>

        {/* 移動・入替ボタン */}
        <button
          onClick={() => (interactionMode === 'move' || interactionMode === 'move-free') ? cancelMoveSwap() : enterMove()}
          style={{
            padding: '4px 10px',
            background: (interactionMode === 'move' || interactionMode === 'move-free') ? '#dc2626' : '#1e293b',
            color: (interactionMode === 'move' || interactionMode === 'move-free') ? '#fff' : '#94a3b8',
            border: '1px solid #334155',
            borderRadius: 6,
            fontSize: 12,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
          title="クリック後に予約を選択→移動先をクリック。またはCmd+Click で即選択"
        >
          {(interactionMode === 'move' || interactionMode === 'move-free') ? '移動中 (Esc)' : '移動'}
        </button>
        <button
          onClick={() => interactionMode === 'swap' ? cancelMoveSwap() : enterSwap()}
          style={{
            padding: '4px 10px',
            background: interactionMode === 'swap' ? '#dc2626' : '#1e293b',
            color: interactionMode === 'swap' ? '#fff' : '#94a3b8',
            border: '1px solid #334155',
            borderRadius: 6,
            fontSize: 12,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
          title="クリック後に予約を2つ順番にクリックして入替"
        >
          {interactionMode === 'swap' ? '入替中 (Esc)' : '入替'}
        </button>

        {/* 車両選択モード */}
        <button
          onClick={() => vehicleSelectMode ? exitVehicleSelectMode() : enterVehicleSelectMode()}
          style={{
            padding: '4px 10px',
            marginLeft: 12,
            background: vehicleSelectMode ? '#7c3aed' : '#1e293b',
            color: vehicleSelectMode ? '#fff' : '#94a3b8',
            border: '1px solid #334155',
            borderRadius: 6,
            fontSize: 12,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
          title="車両ヘッダーをクリックして複数選択し、カスタムフィールドを一括編集"
        >
          {vehicleSelectMode ? `車両選択中 (${selectedVehicleIds.size})` : '車両選択'}
        </button>

        {/* バッチ編集コントロール（車両選択モード時のみ表示） */}
        {vehicleSelectMode && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
              <label style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>ラベル</label>
              <input
                type="text"
                value={batchLabel}
                onChange={e => setBatchLabel(e.target.value)}
                maxLength={10}
                placeholder="例: 損傷"
                style={{
                  width: 64,
                  padding: '3px 6px',
                  fontSize: 11,
                  background: '#1e293b',
                  color: '#e2e8f0',
                  border: '1px solid #475569',
                  borderRadius: 4,
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <label style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>マーク</label>
              <input
                type="color"
                value={batchMark || '#ff0000'}
                onChange={e => setBatchMark(e.target.value)}
                style={{ width: 24, height: 20, border: '1px solid #475569', borderRadius: 3, cursor: 'pointer', padding: 0, background: 'none' }}
              />
              {batchMark && <button onClick={() => setBatchMark('')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 10, padding: 0 }}>✕</button>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <label style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>背景色</label>
              <input
                type="color"
                value={batchBgColor || '#3b82f6'}
                onChange={e => setBatchBgColor(e.target.value)}
                style={{ width: 24, height: 20, border: '1px solid #475569', borderRadius: 3, cursor: 'pointer', padding: 0, background: 'none' }}
              />
              {batchBgColor && <button onClick={() => setBatchBgColor('')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 10, padding: 0 }}>✕</button>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <label style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>文字色</label>
              <input
                type="color"
                value={batchTextColor || '#ffffff'}
                onChange={e => setBatchTextColor(e.target.value)}
                style={{ width: 24, height: 20, border: '1px solid #475569', borderRadius: 3, cursor: 'pointer', padding: 0, background: 'none' }}
              />
              {batchTextColor && <button onClick={() => setBatchTextColor('')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 10, padding: 0 }}>✕</button>}
            </div>
            <button
              onClick={handleBatchSaveClick}
              disabled={selectedVehicleIds.size === 0 || isBatchSaving}
              style={{
                padding: '4px 12px',
                background: selectedVehicleIds.size === 0 || isBatchSaving ? '#334155' : '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: selectedVehicleIds.size === 0 || isBatchSaving ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {isBatchSaving ? '保存中...' : '保存'}
            </button>
          </>
        )}

        {/* ショートカットヘルプ */}
        <button
          onClick={() => setShowShortcutHelp(s => !s)}
          style={{
            width: 24, height: 24, borderRadius: '50%',
            background: showShortcutHelp ? '#3b82f6' : '#1e293b',
            color: showShortcutHelp ? '#fff' : '#94a3b8',
            border: '1px solid #334155', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
          }}
          title="ショートカット一覧"
        >?</button>

        {/* ズーム */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            onClick={() => handleZoom(Math.max(ZOOM_MIN, +(zoom - ZOOM_STEP).toFixed(1)))}
            disabled={zoom <= ZOOM_MIN}
            style={{ ...toolbarBtnStyle, padding: '3px 6px', fontSize: 14 }}
          >−</button>
          <span
            style={{ fontSize: 11, color: '#94a3b8', minWidth: 36, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
            onClick={() => handleZoom(1)}
            title="リセット"
          >{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => handleZoom(Math.min(ZOOM_MAX, +(zoom + ZOOM_STEP).toFixed(1)))}
            disabled={zoom >= ZOOM_MAX}
            style={{ ...toolbarBtnStyle, padding: '3px 6px', fontSize: 14 }}
          >+</button>
        </div>

        <button
          onClick={handleTodayClick}
          style={{
            padding: '4px 12px',
            background: '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          今日
        </button>
      </div>

      {/* 統合 Canvas — 相対配置でUndoバッジとモードバナーをオーバーレイ */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* 車両選択モードバナー */}
        {vehicleSelectMode && (
          <div
            style={{
              position: 'absolute',
              top: GRID_CONFIG.headerHeight,
              left: 0,
              right: 0,
              zIndex: 41,
              padding: '5px 12px',
              background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
              backdropFilter: 'blur(4px)',
              fontSize: 12,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              pointerEvents: 'auto',
            }}>
            車両ヘッダーをクリックして選択/解除 ・ Escで終了
            <button onClick={exitVehicleSelectMode} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
        )}

        {/* 移動/入替モード説明バナー（グリッドヘッダーの下にオーバーレイ） */}
        {interactionMode !== 'none' && (
          <div
            style={{
            position: 'absolute',
            top: GRID_CONFIG.headerHeight,
            left: 0,
            right: 0,
            zIndex: 40,
            padding: '5px 12px',
            background: interactionMode === 'vehicle-swap' ? 'rgba(249,115,22,0.9)'
              : (interactionMode === 'move' || interactionMode === 'move-free') ? 'rgba(220,38,38,0.85)'
              : 'rgba(180,130,8,0.85)',
            backdropFilter: 'blur(4px)',
            fontSize: 12,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            pointerEvents: 'auto',
          }}>
            {interactionMode === 'vehicle-swap'
              ? vehicleSwapSource
                ? `車両入替 ② 「${vehicleSwapSource.vehicle_code}」→ 入替先の車両ヘッダーをクリック`
                : `車両入替 ① 入替元の車両ヘッダーをクリック`
              : (interactionMode === 'move' || interactionMode === 'move-free')
              ? moveSwapSelected
                ? `${interactionMode === 'move-free' ? '自由移動' : '移動'} ② 「${moveSwapSelected.customer_name || '予約'}」→ 移動先をクリック`
                : `移動 ① 移動する予約をクリック`
              : moveSwapSelected
                ? `入替 ② 「${moveSwapSelected.customer_name || '予約'}」→ 入替先をクリック`
                : `入替 ① 入替元の予約をクリック`
            }
            <button onClick={cancelMoveSwap} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
        )}

        <GridCanvas
          containerRef={containerRef}
          vehicles={filteredVehicles}
          reservations={reservations}
          rangeStart={rangeStart}
          dayCount={totalDays}
          vehicleStatusList={vehicleStatusList}
          reservationStatusList={reservationStatusList}
          vehicleTypes={vehicleTypes}
          todayScrollRef={todayScrollRef}
          highlightedReservationId={highlightedReservationId}
          highlightedVehicleIndex={finderVehicle?.index ?? null}
          interactionMode={interactionMode}
          selectedReservationId={
            moveSwapSelected?.id ?? editing?.id ?? null
          }
          ghostPosition={ghostPosition}
          dragGhost={dragGhost}
          resizeGhost={resizeGhost ? {
            vehicleIndex: resizeGhost.vehicleIndex,
            absoluteY: resizeGhost.absTop,
            blockHeight: resizeGhost.absBottom - resizeGhost.absTop,
          } : null}
          zoom={zoom}
          selectedVehicleId={vehicleSwapSource ? String(vehicleSwapSource.id) : null}
          hoveredReservationId={hoveredReservationId}
          onHoverReservationChange={setHoveredReservationId}
          hoveredHeaderIndex={hoveredHeaderIndex}
          onHoverHeaderChange={setHoveredHeaderIndex}
          editingGhost={editingGhost}
          headerDragIndex={headerDragIndex}
          headerDropTarget={headerDropTarget}
          loadedRanges={loadedRanges}
          selectedVehicleIds={vehicleSelectMode ? selectedVehicleIds : null}
          vehicleSelectMode={vehicleSelectMode}
        />

        {/* 車両ヘッダー hover 時ツールチップ（メモ・ラベル・利用期間） */}
        {hoveredHeaderIndex != null && (() => {
          const hv = filteredVehicles[hoveredHeaderIndex];
          if (!hv) return null;
          const memo = (hv as any)?.memo;
          const jd = (hv as any)?.json_data?.data;
          const label = jd?.label;
          const activeFrom = (hv as any)?.active_from;
          const activeTo = (hv as any)?.active_to;
          const hasPeriod = activeFrom || activeTo;
          if (!memo && !label && !hasPeriod) return null;
          const z = zoom || 1;
          const { columnWidth: cw, leftGutterWidth: gw, headerHeight: hh } = GRID_CONFIG;
          const containerEl = containerRef.current;
          const scrollX = containerEl ? containerEl.scrollLeft : 0;
          // ホバー車両列の中央X座標
          const colLeft = gw * z + hoveredHeaderIndex * cw * z - scrollX;
          const tipX = colLeft + (cw * z) / 2;
          const clampedLeft = Math.max(gw * z + 4, Math.min(tipX, (containerEl?.clientWidth ?? 600) - 120));
          return (
            <div
              style={{
                position: 'absolute',
                top: hh * z + 2,
                left: clampedLeft,
                transform: 'translateX(-50%)',
                zIndex: 50,
                maxWidth: 240,
                minWidth: 100,
                padding: '6px 10px',
                background: 'rgba(15, 23, 42, 0.95)',
                color: '#e2e8f0',
                fontSize: 11,
                lineHeight: '1.4',
                borderRadius: 6,
                border: '1px solid #334155',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                pointerEvents: 'none',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {label && (
                <div style={{ marginBottom: memo || hasPeriod ? 4 : 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 10, color: '#94a3b8' }}>ラベル </span>
                  <span style={{ color: '#fbbf24' }}>{label}</span>
                </div>
              )}
              {hasPeriod && (
                <div style={{ marginBottom: memo ? 4 : 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 10, color: '#94a3b8' }}>利用期間 </span>
                  <span>{activeFrom || '?'} 〜 {activeTo || '?'}</span>
                </div>
              )}
              {memo && (
                <div style={{ maxHeight: 80, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 600, fontSize: 10, color: '#94a3b8', marginBottom: 1 }}>メモ</div>
                  {memo.length > 100 ? memo.slice(0, 100) + '…' : memo}
                </div>
              )}
            </div>
          );
        })()}

        {/* バッチ保存確認ダイアログ */}
        {batchConfirmOpen && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} onClick={() => setBatchConfirmOpen(false)}>
            <div style={{
              background: '#fff', borderRadius: 10, padding: '20px 24px',
              minWidth: 280, maxWidth: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }} onClick={e => e.stopPropagation()}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>どの項目に適用しますか？</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                {selectedVehicleIds.size}台の車両に適用します
              </div>
              {([
                ['label', 'ラベル', batchLabel],
                ['mark', 'マーク', batchMark],
                ['bg_color', '背景色', batchBgColor],
                ['text_color', '文字色', batchTextColor],
              ] as [string, string, string][]).map(([key, name, val]) => (
                <label key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 0', cursor: 'pointer', fontSize: 13,
                }}>
                  <input
                    type="checkbox"
                    checked={(batchApplyFlags as any)[key]}
                    onChange={e => setBatchApplyFlags(prev => ({ ...prev, [key]: e.target.checked }))}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ flex: 1 }}>{name}</span>
                  {(key === 'mark' || key === 'bg_color' || key === 'text_color') && val ? (
                    <span style={{
                      display: 'inline-block', width: 16, height: 16,
                      background: val, borderRadius: 3, border: '1px solid #e2e8f0',
                    }} />
                  ) : key === 'label' ? (
                    <span style={{ color: '#64748b', fontSize: 12 }}>{val || '(空)'}</span>
                  ) : null}
                </label>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setBatchConfirmOpen(false)}
                  style={{
                    padding: '6px 16px', fontSize: 13, borderRadius: 6,
                    border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer',
                  }}
                >キャンセル</button>
                <button
                  onClick={executeBatchSave}
                  disabled={!Object.values(batchApplyFlags).some(Boolean)}
                  style={{
                    padding: '6px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6,
                    border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer',
                    opacity: Object.values(batchApplyFlags).some(Boolean) ? 1 : 0.5,
                  }}
                >適用</button>
              </div>
            </div>
          </div>
        )}

        {/* Undo バッジ */}
        {undoAction && (
          <div style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: 8,
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            zIndex: 50,
          }}>
            <button
              onClick={async () => { await undoAction.execute(); dismissUndo(); }}
              style={{
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              元に戻す
            </button>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>{undoAction.label}</span>
            <button onClick={dismissUndo} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
        )}
      </div>

      {/* 編集パネル */}
      <ReservationEditorPanel
        open={!!editing}
        editing={editing as any}
        disabled={isSaving}
        reservationStatusList={reservationStatusList}
        vehicles={vehicleMap}
        vehicleTypes={vehicleTypes}
        onChange={(patch: Partial<EditingReservation>) => setEditing(prev => prev ? { ...prev, ...patch } : null)}
        onCancel={() => setEditing(null)}
        onSave={(_data: any, opts?: { keepOpen?: boolean }) => handleSave(opts ?? {})}
        registerNameInputRef={() => {}}
        onAssignClick={() => {}}
      />

      {/* 車両パネル */}
      <VehicleDetailPanel
        open={!!vehiclePanel}
        vehicle={vehiclePanel}
        onClose={() => setVehiclePanel(null)}
        onSave={handleVehicleSave}
        onDirtyChange={setVehiclePanelDirty}
        vehicleStatusList={vehicleStatusList}
        locationStatusList={locationStatusList}
        onAssignClick={undefined}
      />

      {/* 車両入替確認モーダル */}
      {vehicleSwapInfo && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 3900,
          background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '20px 24px', maxWidth: 520, width: '90%',
            maxHeight: '80vh', overflow: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>車両入替の確認</h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, fontSize: 14 }}>
              <div style={{ flex: 1, textAlign: 'center', padding: '8px 12px', background: '#f1f5f9', borderRadius: 8 }}>
                <div style={{ fontWeight: 700 }}>{vehicleSwapInfo.vehicleA.vehicle_code}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{vehicleSwapInfo.vehicleA.vehicle_type?.name}</div>
              </div>
              <span style={{ fontSize: 20 }}>↔</span>
              <div style={{ flex: 1, textAlign: 'center', padding: '8px 12px', background: '#f1f5f9', borderRadius: 8 }}>
                <div style={{ fontWeight: 700 }}>{vehicleSwapInfo.vehicleB.vehicle_code}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{vehicleSwapInfo.vehicleB.vehicle_type?.name}</div>
              </div>
            </div>

            <div style={{ fontSize: 13, marginBottom: 12, color: '#334155' }}>
              <div>入替対象: <strong>{vehicleSwapInfo.swapTargetsA.length + vehicleSwapInfo.swapTargetsB.length}件</strong></div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {vehicleSwapInfo.vehicleA.vehicle_code}: {vehicleSwapInfo.swapTargetsA.length}件 / {vehicleSwapInfo.vehicleB.vehicle_code}: {vehicleSwapInfo.swapTargetsB.length}件
              </div>
              {vehicleSwapInfo.excludedA.length + vehicleSwapInfo.excludedB.length > 0 && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  除外（過去/進行中）: {vehicleSwapInfo.excludedA.length + vehicleSwapInfo.excludedB.length}件
                </div>
              )}
            </div>

            {(vehicleSwapInfo.warningsA.length > 0 || vehicleSwapInfo.warningsB.length > 0) && (
              <div style={{
                background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6, color: '#92400e' }}>
                  ⚠ 注意が必要な予約（{vehicleSwapInfo.warningsA.length + vehicleSwapInfo.warningsB.length}件）
                </div>
                <div style={{ fontSize: 11, color: '#78350f', marginBottom: 8 }}>
                  以下の予約にはメンテナンス関連のキーワード（車検・点検・修理・タイヤ・指定）が含まれています。
                </div>
                {[...vehicleSwapInfo.warningsA, ...vehicleSwapInfo.warningsB].map(r => (
                  <div key={r.id} style={{ padding: '3px 0', borderBottom: '1px solid #fde68a', fontSize: 11 }}>
                    <strong>{r.customer_name}</strong>
                    {' '}({new Date(r.start_date).toLocaleDateString('ja')}〜{new Date(r.end_date).toLocaleDateString('ja')})
                  </div>
                ))}
              </div>
            )}

            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 16 }}>
              未来の予約の車両アサインを相互に入れ替えます。過去の予約と進行中（貸出中/貸出済み）の予約は入れ替わりません。
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={cancelVehicleSwap}
                style={{ padding: '6px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
              >
                キャンセル
              </button>
              <button
                onClick={executeVehicleSwap}
                style={{ padding: '6px 16px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                入替を実行（{vehicleSwapInfo.swapTargetsA.length + vehicleSwapInfo.swapTargetsB.length}件）
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ショートカットヘルプモーダル */}
      {showShortcutHelp && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
          onClick={() => setShowShortcutHelp(false)}
        >
          <div
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 24, maxWidth: 500, width: '90%', color: '#e2e8f0' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700 }}>ショートカット一覧</h3>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #334155' }}>
                  <th style={{ padding: '6px 12px 6px 0', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>操作</th>
                  <th style={{ padding: '6px 0', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>機能</th>
                </tr>
              </thead>
              <tbody>
                {([
                  ['クリック', '予約移動（水平）'],
                  ['Alt(Option) + クリック', '予約移動（自由）'],
                  ['Shift + クリック', '予約入替'],
                  ['ダブルクリック', '予約編集パネル'],
                  ['ドラッグ（空セル）', '新規予約作成'],
                  ['上下端ドラッグ', '日時変更（リサイズ）'],
                  ['⇧⌘ + ヘッダークリック', '車両スワップ'],
                  ['ヘッダー D&D', '車両並べ替え'],
                  ['Escape', 'モードキャンセル'],
                ] as [string, string][]).map(([key, desc]) => (
                  <tr key={key} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '8px 12px 8px 0', color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                      <code style={{ background: '#0f172a', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>{key}</code>
                    </td>
                    <td style={{ padding: '8px 0' }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3 style={{ margin: '20px 0 10px', fontSize: 15, fontWeight: 700, borderTop: '1px solid #334155', paddingTop: 16 }}>車両選択モード</h3>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 8px', lineHeight: '1.6' }}>
              複数の車両にラベル・マーク・背景色・文字色をまとめて設定できます。
            </p>
            <div style={{ fontSize: 12, lineHeight: '1.8' }}>
              <div><strong style={{ color: '#cbd5e1' }}>1.</strong> ツールバーの<code style={{ background: '#0f172a', padding: '2px 6px', borderRadius: 4, color: '#a78bfa' }}>車両選択</code>ボタンをクリック</div>
              <div><strong style={{ color: '#cbd5e1' }}>2.</strong> 車両ヘッダーをクリックして選択（複数可・再クリックで解除）</div>
              <div><strong style={{ color: '#cbd5e1' }}>3.</strong> ラベル・マーク・背景色・文字色を入力</div>
              <div><strong style={{ color: '#cbd5e1' }}>4.</strong> <code style={{ background: '#0f172a', padding: '2px 6px', borderRadius: 4, color: '#60a5fa' }}>保存</code>→ 適用項目を選んで一括更新</div>
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10, lineHeight: '1.6' }}>
              <div>● <strong style={{ color: '#cbd5e1' }}>ラベル</strong>: ヘッダー左上に三角バッジ表示（先頭3文字）</div>
              <div>● <strong style={{ color: '#cbd5e1' }}>マーク</strong>: ヘッダー右上にカラードット表示</div>
              <div>● <strong style={{ color: '#cbd5e1' }}>背景色/文字色</strong>: 車両コードの背景・文字に適用</div>
            </div>
            <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 0', lineHeight: '1.5' }}>
              ※ ヘッダーにマウスを重ねると、メモ・ラベル・利用期間が表示されます。
            </p>

            <button
              onClick={() => setShowShortcutHelp(false)}
              style={{ marginTop: 16, padding: '6px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >閉じる</button>
          </div>
        </div>
      )}

      {/* トースト通知 */}
      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.onUndo ? 5000 : 3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.severity ?? 'success'}
          variant="filled"
          sx={{ fontSize: 13, display: 'flex', alignItems: 'center' }}
          action={toast?.onUndo ? (
            <button
              onClick={() => { toast.onUndo?.(); setToast(null); }}
              style={{
                background: 'rgba(255,255,255,0.25)',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '2px 10px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                marginLeft: 8,
              }}
            >
              元に戻す
            </button>
          ) : undefined}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

const toolbarBtnStyle: React.CSSProperties = {
  padding: '3px 8px',
  background: '#1e293b',
  color: '#e2e8f0',
  border: '1px solid #334155',
  borderRadius: 4,
  fontSize: 12,
  cursor: 'pointer',
};
